import React, { useState, useEffect, useCallback } from 'react';

const BACKEND = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin);

const CATEGORIES = ['wallpaper', 'tile', 'fabric', 'stone', 'flooring', 'trim', 'other'];

/**
 * MaterialLibraryManager — load a supplier catalogue into the searchable
 * material library, and see/remove what is already loaded.
 *
 * Supplier catalogues (York, Phillip Jeffries, ...) are not ours to ship, so
 * they are imported from a CSV the studio exports or is sent. Paint is
 * different: that catalogue is curated and already in the app.
 */
export default function MaterialLibraryManager({ onClose, onImported }) {
  const [library, setLibrary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);   // { tone: 'ok'|'error', text }

  const [category, setCategory] = useState('wallpaper');
  const [manufacturer, setManufacturer] = useState('');
  const [csv, setCsv] = useState('');
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState(null);   // { headers, rows, total }

  const loadLibrary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/materials/categories`);
      if (!res.ok) throw new Error(`Could not read the library (${res.status})`);
      setLibrary(await res.json());
    } catch (e) {
      setMessage({ tone: 'error', text: e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLibrary(); }, [loadLibrary]);

  /** Parse enough of the CSV to show what will be imported before committing. */
  const makePreview = (text) => {
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (!lines.length) return null;
    const split = (line) => line.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
    const headers = split(lines[0]);
    return {
      headers,
      rows: lines.slice(1, 6).map(split),
      total: lines.length - 1,
    };
  };

  const onFile = (file) => {
    if (!file) return;
    setFileName(file.name);
    setMessage(null);
    const fr = new FileReader();
    fr.onload = () => {
      const text = String(fr.result || '');
      setCsv(text);
      setPreview(makePreview(text));
    };
    fr.onerror = () => setMessage({ tone: 'error', text: `Could not read ${file.name}` });
    fr.readAsText(file);
  };

  const doImport = async () => {
    if (!csv.trim()) {
      setMessage({ tone: 'error', text: 'Choose a CSV file first, or paste its contents.' });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const res = await fetch(`${BACKEND}/api/materials/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category, manufacturer, csv }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `Import failed (${res.status})`);
      setMessage({
        tone: 'ok',
        text: `${data.added} added, ${data.updated} updated${data.skipped ? `, ${data.skipped} skipped (no name)` : ''}. ` +
          `${data.total_in_category} ${data.category} now searchable.`,
      });
      setCsv(''); setFileName(''); setPreview(null);
      await loadLibrary();
      if (onImported) onImported();
    } catch (e) {
      setMessage({ tone: 'error', text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const removeSet = async (cat, brand, count) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Remove all ${count} imported "${brand}" ${cat} entries from the library?\n\nAnything already used on a sheet or a room stays where it is.`)) return;
    setBusy(true);
    try {
      const qs = new URLSearchParams({ category: cat, manufacturer: brand });
      const res = await fetch(`${BACKEND}/api/material-library/import?${qs}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `Could not remove that set (${res.status})`);
      setMessage({ tone: 'ok', text: `Removed ${data.deleted} entries.` });
      await loadLibrary();
      if (onImported) onImported();
    } catch (e) {
      setMessage({ tone: 'error', text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const input = 'w-full bg-black/40 border border-[#D4A574]/30 rounded px-2 py-1.5 text-white text-sm';
  const cats = library?.categories || {};

  return (
    <div className="fixed inset-0 bg-black/90 z-[70] overflow-y-auto p-4">
      <div className="max-w-3xl mx-auto bg-[#0F172A] border border-[#D4A574]/40 rounded-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#D4A574]/30">
          <div>
            <h2 className="text-xl font-bold text-[#D4A574]">📚 Material Library</h2>
            <p className="text-[11px] text-[#B49B7E]">
              Load a supplier catalogue so it can be found by typing anywhere in the app.
            </p>
          </div>
          <button onClick={onClose} className="px-3 py-2 text-gray-300 hover:text-white text-sm" data-testid="mlm-close">✕ Close</button>
        </div>

        <div className="p-6 space-y-6">
          {message && (
            <p className={`text-sm ${message.tone === 'ok' ? 'text-emerald-400' : 'text-red-400'}`}
              data-testid="mlm-message">{message.text}</p>
          )}

          {/* IMPORT */}
          <section className="space-y-3">
            <h3 className="text-sm font-bold text-[#D4C5A9] uppercase tracking-wide">Import a catalogue</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-[11px] text-[#B49B7E] mb-1">Category</span>
                <select value={category} onChange={e => setCategory(e.target.value)}
                  className={input} data-testid="mlm-category">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="block text-[11px] text-[#B49B7E] mb-1">Manufacturer / brand</span>
                <input value={manufacturer} onChange={e => setManufacturer(e.target.value)}
                  placeholder="e.g. Phillip Jeffries" className={input} data-testid="mlm-manufacturer" />
              </label>
            </div>

            <p className="text-[11px] text-[#B49B7E]">
              The file needs a header row. <code className="text-[#D4A574]">name</code> is required;
              {' '}<code className="text-[#D4A574]">sku</code>, <code className="text-[#D4A574]">collection</code>,
              {' '}<code className="text-[#D4A574]">color</code>, <code className="text-[#D4A574]">size</code>,
              {' '}<code className="text-[#D4A574]">image_url</code> and <code className="text-[#D4A574]">link</code> are
              used if present. Re-importing a corrected file updates rows rather than duplicating them.
            </p>

            <label className={`block text-center px-3 py-2 rounded text-sm font-medium ${busy ? 'bg-slate-800 text-gray-500' : 'bg-amber-700 hover:bg-amber-600 text-white cursor-pointer'}`}>
              {fileName || '⬆️ Choose a CSV file'}
              <input type="file" accept=".csv,text/csv,text/plain" hidden disabled={busy}
                data-testid="mlm-file"
                onChange={e => { onFile(e.target.files?.[0]); e.target.value = ''; }} />
            </label>

            {preview && (
              <div className="border border-[#D4A574]/30 rounded p-3 overflow-x-auto" data-testid="mlm-preview">
                <p className="text-[11px] text-[#D4C5A9] mb-2">
                  {preview.total} row(s) found. First few:
                  {!preview.headers.some(h => h.toLowerCase() === 'name') && (
                    <span className="text-amber-400"> — no “name” column, so rows will be skipped.</span>
                  )}
                </p>
                <table className="text-[11px] w-full">
                  <thead>
                    <tr className="text-[#B49B7E]">
                      {preview.headers.map((h, i) => <th key={i} className="text-left pr-3 pb-1">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r, i) => (
                      <tr key={i} className="text-white">
                        {r.map((c, j) => <td key={j} className="pr-3 truncate max-w-[160px]">{c}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <button onClick={doImport} disabled={busy || !csv.trim()}
              data-testid="mlm-import"
              className="px-5 py-2.5 bg-[#D4A574] hover:bg-[#E2B989] disabled:opacity-40 text-black rounded font-bold">
              {busy ? 'Importing…' : 'Import into the library'}
            </button>
          </section>

          {/* WHAT IS LOADED */}
          <section className="border-t border-[#D4A574]/20 pt-5">
            <h3 className="text-sm font-bold text-[#D4C5A9] uppercase tracking-wide mb-3">Already in the library</h3>
            {loading ? (
              <p className="text-sm text-[#D4C5A9]">Loading…</p>
            ) : Object.keys(cats).length === 0 ? (
              <p className="text-sm text-gray-500 italic">Nothing yet.</p>
            ) : (
              <div className="space-y-3" data-testid="mlm-library">
                {Object.entries(cats).map(([cat, brands]) => (
                  <div key={cat}>
                    <p className="text-xs text-[#D4A574] uppercase tracking-wide mb-1">{cat}</p>
                    {Object.keys(brands).length === 0 ? (
                      <p className="text-[11px] text-gray-500 italic ml-2">none</p>
                    ) : (
                      <ul className="ml-2 space-y-1">
                        {Object.entries(brands).map(([brand, count]) => (
                          <li key={brand} className="flex items-center gap-3 text-sm text-white">
                            <span className="flex-1 truncate">{brand}</span>
                            <span className="text-[#B49B7E] text-xs whitespace-nowrap">{count} item(s)</span>
                            {cat === 'paint' ? (
                              <span className="text-[10px] text-[#B49B7E] uppercase">built in</span>
                            ) : (
                              <button onClick={() => removeSet(cat, brand, count)} disabled={busy}
                                data-testid={`mlm-remove-${cat}-${brand}`}
                                className="text-[11px] text-red-400 hover:text-red-300 disabled:opacity-40">Remove</button>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
            {library?.curated_paint_colours ? (
              <p className="text-[11px] text-[#B49B7E] mt-3">
                {library.curated_paint_colours} paint colours (Benjamin Moore, Sherwin-Williams,
                Farrow &amp; Ball) are built in and always searchable.
              </p>
            ) : null}
          </section>
        </div>
      </div>
    </div>
  );
}
